'use client';

import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { getSnowRating, snowRatingColors } from '@/data/weather';
import {
  resolveWeatherLabels,
  type WeatherLabels,
} from '@/components/weather/labels';

interface SnowDepthGaugeProps {
  depth: number;
  label?: string;
  sublabel?: string;
  noData?: boolean;
  labels?: WeatherLabels;
}

function fillColor(depth: number): string {
  if (depth < 20) return '#94A3B8';
  if (depth < 40) return '#93C5FD';
  if (depth < 70) return '#3B82F6';
  if (depth < 100) return '#6366F1';
  return '#8B5CF6';
}

export default function SnowDepthGauge({ depth, label, sublabel, noData, labels }: SnowDepthGaugeProps) {
  const L = resolveWeatherLabels(labels);
  const maxScale = 150;
  const fillPercent = Math.min((depth / maxScale) * 100, 100);
  const color = fillColor(depth);
  const rating = getSnowRating(depth);
  const ratingColor = snowRatingColors[rating];

  return (
    <div className="flex flex-col items-center">
      {/* Gauge container */}
      <div
        className={cn(
          'relative overflow-hidden rounded-xl border',
          'border-gray-300 bg-[#F0EDE5]',
        )}
        style={{ width: 80, height: 200 }}
      >
        {/* Fill bar from bottom */}
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: `${fillPercent}%` }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="absolute bottom-0 left-0 right-0 rounded-b-lg"
          style={{
            background: `linear-gradient(to top, ${color}, ${color}CC)`,
          }}
        />

        {/* Depth number overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {noData && depth === 0 ? (
            <span
              className={cn(
                'text-sm font-medium drop-shadow-md',
                'text-[#5A5A6E]',
              )}
            >
              {L.gaugeNoData}
            </span>
          ) : (
            <>
              <span
                className={cn(
                  'text-2xl font-bold drop-shadow-md',
                  'text-[#1A1A2E]',
                )}
              >
                {Math.round(depth)}
              </span>
              <span
                className={cn(
                  'text-xs font-medium drop-shadow-md',
                  'text-[#5A5A6E]',
                )}
              >
                cm
              </span>
            </>
          )}
        </div>
      </div>

      {/* Rating badge */}
      <span
        className="mt-3 rounded-full px-3 py-0.5 text-xs font-semibold uppercase tracking-wide"
        style={{ backgroundColor: `${ratingColor}20`, color: ratingColor }}
      >
        {L.ratings[rating]}
      </span>

      {/* Label */}
      <p
        className={cn(
          'mt-2 text-center text-sm font-medium',
          'text-[#1A1A2E]',
        )}
      >
        {label || 'Kettura Snow Index'}
      </p>

      {/* Sub-label */}
      <p
        className={cn(
          'mt-0.5 text-center text-xs',
          'text-[#5A5A6E]',
        )}
      >
        {sublabel || 'Mean base depth \u2014 Levi & Ylläs'}
      </p>
    </div>
  );
}
